import { useEffect, useState } from 'react'
import { SEO } from './SEO'
import { firebaseConfig } from '../../lib/firebaseConfig'
import { getSeoEntry, routeKeyFor, DEFAULT_SEO, type SeoOgType } from '../../config/seo'

interface ManagedSeoProps {
  /** Route path; also the key into the static registry and the override doc. */
  path: string
  /** Page-specific JSON-LD passed straight through (not managed/editable). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** Optional OG image override passed straight through. */
  image?: string
  /** Keep out of search results. Passed straight through, not managed. */
  noindex?: boolean
}

interface ResolvedSeo {
  title: string
  description: string
  ogType: SeoOgType
}

const OG_TYPES: SeoOgType[] = ['website', 'article', 'product', 'profile', 'event', 'service']

function isOgType(v: unknown): v is SeoOgType {
  return typeof v === 'string' && (OG_TYPES as string[]).includes(v)
}

function cleanString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null
}

// The instant, never-blank default: the static registry entry for this route,
// falling back to the site-wide DEFAULT_SEO for any unmanaged/dynamic route.
function staticBaseFor(path: string): ResolvedSeo {
  const entry = getSeoEntry(path)
  return {
    title: entry?.title.trim() || DEFAULT_SEO.title,
    description: entry?.description.trim() || DEFAULT_SEO.description,
    ogType: entry?.ogType ?? DEFAULT_SEO.ogType,
  }
}

// Firestore REST document shape (only the value kinds we read).
interface RestDocument {
  fields?: Record<string, { stringValue?: string }>
}

const SEO_DOC_URL =
  `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}` +
  `/databases/(default)/documents/seoSettings/`

// One plain GET against the Firestore REST API. The Firestore SDK was the
// only reason public pages loaded ~100 KB of firestore + webchannel code and
// opened a streaming Listen channel on every visit (Lighthouse, Sep 2026).
// seoSettings is public-read in firestore.rules, so no auth is needed.
async function fetchSeoOverride(path: string): Promise<RestDocument | null> {
  const url = `${SEO_DOC_URL}${encodeURIComponent(routeKeyFor(path))}?key=${firebaseConfig.apiKey}`
  const res = await fetch(url)
  if (!res.ok) return null
  return (await res.json()) as RestDocument
}

// Signals scripts/prerender.mjs that the override for this route has settled
// (found or not), so the baked HTML carries the managed title/description.
function markSeoReady(path: string): void {
  document.documentElement.setAttribute('data-seo-ready', path)
}

// Hybrid SEO. Renders the static registry default immediately (synchronous,
// so meta is correct on first paint and never blanks), then asynchronously
// applies the Firestore seoSettings override for this route if one exists.
// A missing doc, blank field, or read error leaves the static default in place.
export function ManagedSeo({ path, jsonLd, image, noindex }: ManagedSeoProps) {
  const [resolved, setResolved] = useState<ResolvedSeo>(() => staticBaseFor(path))

  useEffect(() => {
    let cancelled = false
    // Reset to the static default on route change so a slow or failed override
    // never leaves stale meta from the previous route.
    const base = staticBaseFor(path)
    setResolved(base)
    document.documentElement.removeAttribute('data-seo-ready')

    fetchSeoOverride(path)
      .then((docData) => {
        if (cancelled) return
        const fields = docData?.fields
        if (!fields) return
        setResolved({
          title: cleanString(fields.title?.stringValue) ?? base.title,
          description: cleanString(fields.description?.stringValue) ?? base.description,
          ogType: isOgType(fields.ogType?.stringValue) ? fields.ogType!.stringValue as SeoOgType : base.ogType,
        })
      })
      .catch(() => {
        // Offline / blocked / etc. Keep the static default. Never blank.
      })
      .finally(() => {
        if (!cancelled) markSeoReady(path)
      })

    return () => {
      cancelled = true
    }
  }, [path])

  return (
    <SEO
      title={resolved.title}
      description={resolved.description}
      path={path}
      ogType={resolved.ogType}
      image={image}
      jsonLd={jsonLd}
      noindex={noindex}
    />
  )
}
